/**
 * RUM Event Helpers - A3S Frontend Metrics
 *
 * Comprehensive RUM event tracking for A3S application usage metrics
 */

// RUM Event Types
export const RUM_EVENT_TYPES = {
    APPLICATION_LAUNCHED: "app-launch-business-event",
    SANDBOX_SELECTED: "sandbox-selected-business-event",
    ALLOCATION_TYPE_SELECTED: "allocation-type-selected-business-event",
    SIMULATION_CREATED: "simulation-created-business-event",
    SCENARIO_SELECTED: "scenario-selected-business-event",
    DRIVER_PREVIEWED: "driver-previewed-business-event",
    DRIVER_APPLIED: "driver-applied-business-event",
    DRIVER_UPLOADED: "driver-uploaded-business-event",
    SIMULATION_STARTED: "simulation-started-business-event",
    SIMULATION_RESULTS: "simulation-results-business-event",
    SIMULATION_SHARED_WITH_RFA: "simulation-shared-with-rfa-business-event",
} as const;

// RUM Actions
export const RUM_ACTIONS = {
    LAUNCH: "Launch",
    SANDBOX_SELECT: "SandboxSelect",
    ALLOC_TYPE_SELECT: "AllocTypeSelect",
    CREATE_SIM: "CreateSim",
    SELECT_SCENARIO: "SelectScenario",
    DRIVER_PREVIEW: "DriverPreview",
    DRIVER_APPLY: "DriverApply",
    DRIVER_UPLOAD: "DriverUpload",
    START_SIMULATION: "StartSimulation",
    SEND_TO_RFA: "SendToRFA",

    // QuickSight report actions
    VIEW_SIMULATION_QUICKSIGHT_REPORTS: "ViewSimulationQuickSightReports",

    // FinAltSuite report actions
    VIEW_SIMULATION_FINALT_SUITE_REPORTS: "ViewSimulationFinAltSuiteReports",
    FINALT_SUITE_MAXIMIZE_REPORT: "FinAltSuiteMaximizeReport",
    FINALT_SUITE_MINIMIZE_REPORT: "FinAltSuiteMinimizeReport",
    FINALT_SUITE_REFRESH_REPORT: "FinAltSuiteRefreshReport",
    FINALT_SUITE_REPORT_READY: "FinAltSuiteReportReady",
    FINALT_SUITE_REPORT_ERROR: "FinAltSuiteReportError",
} as const;

// RUM Components
export const RUM_COMPONENTS = {
    APPLICATION: "Application",
    SANDBOX_SELECTOR: "SandboxSelector",
    SIMULATION: "Simulation",
    SIMULATION_RESULTS: "SimulationResults",
    SCENARIO_SELECTOR: "ScenarioSelector",
    DRIVER_MANAGER: "DriverManager",
} as const;

// Base RUM Event Payload Structure
export interface BaseRumEventPayload {
    userName: string;
    timestamp: string;
    action: string;
    component: string;
}

// ============================================================================
// Metrics One Event Design — Standardized Payload for OpenSearch
// ============================================================================

/**
 * Metrics One Event Payload
 * Standardized event structure for OpenSearch index ingestion.
 */
export interface BusinessEventPayload {
    /** User action, e.g. "app_launch", "button_click" */
    action: string;
    /** Action context/identifier, e.g. "APPLICATION_LAUNCHED", "SHOW_EVENT_DETAILS" */
    action_identifier: string;
    /** User alias (login username) */
    user_alias: string;
    /** Current page full URL */
    url: string;
    /** Current page domain */
    domain: string;
    /** ISO 8601 timestamp */
    timestamp: string;
    /** Session ID — persisted across reloads via sessionStorage, reset on tab/window closure */
    session_id: string;
    /** Device information */
    device_info: {
        timezone: string;
        browser: string;
        os: string;
        device_type: string;
    };
    /** Open JSON for any additional data needed for metrics */
    metadata?: Record<string, any>;
}

/**
 * Get or create a session ID.
 * Persisted in sessionStorage so it survives page reloads but resets on tab/window closure.
 */
const SESSION_STORAGE_KEY = "metrics_one_session_id";

function getSessionId(): string {
    let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
        sessionId = crypto.randomUUID();
        sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
}

/**
 * Detect device info from the browser's navigator.
 */
function getDeviceInfo(): { timezone: string; browser: string; os: string; device_type: string } {
    const ua = navigator.userAgent;

    // Detect browser
    let browser = "Unknown";
    if (ua.includes("Firefox")) browser = "Firefox";
    else if (ua.includes("Edg/")) browser = "Edge";
    else if (ua.includes("Chrome")) browser = "Chrome";
    else if (ua.includes("Safari")) browser = "Safari";

    // Detect OS
    let os = "Unknown";
    if (ua.includes("Mac OS")) os = "macOS";
    else if (ua.includes("Windows")) os = "Windows";
    else if (ua.includes("Linux")) os = "Linux";
    else if (ua.includes("Android")) os = "Android";
    else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";

    // Detect device type — use \bMobi\b word boundary to avoid matching "Mozilla"
    const isMobile = /\bMobi\b|Android|iPhone|iPad|iPod/.test(ua);
    const device_type = isMobile ? "mobile" : "desktop";

    return { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, browser, os, device_type };
}

/**
 * Build a Metrics One event payload.
 * Auto-populates url, domain, timestamp, and device_info.
 */
export function buildBusinessEventPayload(
    action: string,
    actionIdentifier: string,
    userAlias: string,
    metadata?: Record<string, any>,
): BusinessEventPayload {
    // Strip "Federate_" prefix from Cognito federated sign-in username
    const cleanAlias = userAlias.replace(/^Federate_/i, "");

    return {
        action,
        action_identifier: actionIdentifier,
        user_alias: cleanAlias,
        url: window.location.href,
        domain: window.location.hostname,
        timestamp: new Date().toISOString(),
        session_id: getSessionId(),
        device_info: getDeviceInfo(),
        metadata,
    };
}
